import React, { useId, useState } from 'react'
import { Box, TextFieldProps } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

type CustomDatePickerProps = {
  label?: string
  onChange: (value: Date | null) => void
  value?: Date | null
  dateFormat?: string
  shouldDisableDate?: (date: Date) => boolean
  helperText?: string
  error?: boolean
  required?: boolean
}

type DatePickerTextFieldProps = {
  'data-testid'?: string
  helperText?: string
} & TextFieldProps


const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  label = 'Select date',
  onChange,
  value = null,
  dateFormat = 'MMM d yyyy',
  shouldDisableDate,
  helperText,
  error = false,
  required = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(value)
  const internalId = useId() // generates a stable unique ID for the input
  const testId = `custom-datepicker-${internalId}` // Generate a unique data-testid

  const handleDateChange = (newDate: Date | null) => {
    setSelectedDate(newDate)
    onChange(newDate)
  }

  // Set up textField props for MUI
  const textFieldProps: DatePickerTextFieldProps = {
    fullWidth: true,
    variant: 'outlined',
    size: 'medium',
    id: internalId,
    'aria-labelledby': `${internalId}-label`,
    'data-testid': testId,
    helperText: helperText,
    error,
    required,
    sx: {
      '& .MuiInputBase-input:focus': {
        outline: 'none',
        boxShadow: 'none',
      },
    }
  }

  return (
    <Box sx={{ padding: 2, position: 'relative', minWidth: 138 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={label}
          value={selectedDate}
          onChange={handleDateChange}
          format={dateFormat}
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            textField: textFieldProps,
          }}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default CustomDatePicker
